"use client";

import { useEffect, useRef } from "react";

import { getAttribution } from "@/app/libs/attribution";

interface AnalyticsTrackerProps {
  type: "listing_view" | "search" | "checkout_view";
  listingId?: string;
  metadata?: Record<string, unknown>;
  /** When set, also reports clicks on the liveaboard strip's cards
   * (`data-search-liveaboard` attributes) as `search_click` events, so the
   * strip's CTR can be compared against the listing grid per destination. */
  watchLiveaboardClicksFor?: string;
}

/**
 * Records one analytics event per distinct page view, from the browser.
 * Client-side on purpose: the listing and search pages are crawled by Google
 * for the Things to Do feed, and server-side tracking would count those bots.
 */
const AnalyticsTracker = ({
  type,
  listingId,
  metadata,
  watchLiveaboardClicksFor,
}: AnalyticsTrackerProps) => {
  const signature = JSON.stringify({ type, listingId, metadata });
  const lastSent = useRef<string | null>(null);

  useEffect(() => {
    // Guards against React strict-mode double effects and re-renders that
    // do not change what is being viewed.
    if (lastSent.current === signature) return;
    lastSent.current = signature;

    fetch("/api/track", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ type, listingId, metadata, attribution: getAttribution() }),
      keepalive: true,
    }).catch(() => {
      // Silent fail to avoid affecting UX
    });
  }, [signature]);

  useEffect(() => {
    if (watchLiveaboardClicksFor === undefined) return;

    const onClick = (event: MouseEvent) => {
      const target = event.target as Element | null;
      const card = target?.closest?.("[data-search-liveaboard]");
      if (!card) return;

      try {
        fetch("/api/track", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            type: "search_click",
            metadata: {
              cityOrCountry: watchLiveaboardClicksFor,
              resultType: "liveaboard",
              liveaboardId: card.getAttribute("data-search-liveaboard"),
              position: Number(card.getAttribute("data-search-pos") ?? -1),
            },
            attribution: getAttribution(),
          }),
          keepalive: true,
        }).catch(() => {
          // Silent fail to avoid affecting UX
        });
      } catch {
        // Silent fail to avoid affecting UX
      }
    };

    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [watchLiveaboardClicksFor]);

  return null;
};

export default AnalyticsTracker;
